import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MainRoutes } from "common/config";
import CategoriesOverview from "./CategoriesOverview";
import Loading from "../../../_shared/pages/loading/Loading";
import { ClassificationService } from "../../data/services/classification.service";
import { CategoryCount } from "../../data/models";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import "./CategoriesOverview.scss";

const CategoriesOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { batchGuid: urlBatchGuid } = useParams<{ batchGuid: string }>();

  // State for API data
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [apiTotalFiles, setApiTotalFiles] = useState<number>(0);
  const [queuedCount, setQueuedCount] = useState<number>(0);
  const [completedCount, setCompletedCount] = useState<number>(0);
  const [inProgressCount, setInProgressCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const classificationName =
    location.state?.classificationName || "Classification";
  const documentId = location.state?.documentId;
  const batchGuid = urlBatchGuid || location.state?.batchGuid;

  const backRoute = "/classification-hub/document-history";

  // Function to fetch batch classification summary from API
  const fetchClassificationSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only use batch-specific API - batchGuid is required
      if (!batchGuid) {
        setError("Batch GUID is required for classification summary");
        setCategories([]);
        setLoading(false);
        return;
      }

      const result =
        await ClassificationService.getClassificationSummaryByBatch(batchGuid);

      if (!result.hasErrors && result.value) {
        // Calculate total documents from API totals
        const totals = result.value.documentSummary.totals;
        const totalDocuments =
          totals.completed + totals.inProgress + totals.error + totals.queued;
        setApiTotalFiles(totalDocuments);
        setQueuedCount(totals.queued);
        setCompletedCount(totals.completed);
        setInProgressCount(totals.inProgress);
        setErrorCount(totals.error);

        // Transform API response to CategoryCount format
        const transformedCategories: CategoryCount[] =
          result.value.documentSummary.categories.map(
            (category: any, index: number) => ({
              id: `category-${index}`,
              name: category.name,
              count: category.totalDocuments,
              color: `hsl(${index * 45}, 70%, 50%)`,
              description: `Category with ${category.totalDocuments} documents`
            })
          );

        setCategories(transformedCategories);
      } else {
        const errorMsg =
          result.errors.length > 0
            ? result.errors.join(", ")
            : `Failed to fetch classification summary for batch: ${classificationName}`;
        setError(errorMsg);
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching classification summary:", err);
      const errorMsg = `An error occurred while fetching data for batch: ${classificationName}`;
      setError(errorMsg);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassificationSummary();
  }, []);

  const handleRefresh = async () => {
    await fetchClassificationSummary();
  };

  const handleCategoryClick = (categoryId: string, fileCount: number) => {
    // Navigate to category details page with batchGuid in URL and state
    if (batchGuid) {
      navigate(
        MainRoutes.DATA_CLASSIFICATION_DETAIL.replace(
          ":category",
          categoryId
        ).replace(":batchGuid", batchGuid),
        {
          state: {
            fileCount,
            classificationName: classificationName
          }
        }
      );
    }
  };

  const handleNewUpload = () => {
    navigate(MainRoutes.DATA_CLASSIFICATION_HUB);
  };

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="categories-overview-main">
        {/* Header Section */}
        <HubHeader
          title="Classification Hub"
          description="Classify documents and validate category"
          showBackButton={true}
          backRoute="/classification-hub"
        />
      </div>
      {loading ? (
        <Loading />
      ) : (
        <CategoriesOverview
          categories={categories}
          totalFiles={apiTotalFiles}
          completedCount={completedCount}
          queuedCount={queuedCount}
          inProgressCount={inProgressCount}
          errorCount={errorCount}
          classificationName={classificationName}
          backRoute={backRoute}
          batchGuid={batchGuid}
          onCategoryClick={handleCategoryClick}
          onNewUpload={handleNewUpload}
          onRefresh={handleRefresh}
          onBatchExtractAll={() => {}}
        />
      )}
    </>
  );
};

export default CategoriesOverviewPage;
